import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { EditSocietyModalContent } from './EditSocietyModalContent'
import { adminEditSocietySchema } from '../../../../../forms/schemas/admin_edit_society'
import { formatOrganisationNumber } from '../../../../../helpers/organisation_number'
import { useAdminSociety } from '../useAdminSociety'
import { patchSociety } from '../../../../../api/admin'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'

interface EditSocietyModalProps {
  show: boolean
  close: () => void
  societyId?: string
}

export const EditSocietyModal = ({
  show,
  close,
  societyId,
}: EditSocietyModalProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { adminSociety } = useAdminSociety(societyId)

  const initialOrganisationNumber = adminSociety?.organisationNumber

  const { mutate: mutatePatchSociety } = useMutation({
    mutationFn: patchSociety,
    onSuccess: () => {
      setToastNotification(ToastType.SUCCESS, translate('Förening uppdaterad'))
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })
  const onSubmit = async (
    data: InferType<typeof adminEditSocietySchema>
  ): Promise<void> => {
    const { organisationNumber, ...rest } = data
    // Avoid sending organisationNumber if it hasn't been changed, backend doesn't allow already existing
    // organisation numbers
    const dataToSend =
      initialOrganisationNumber &&
      organisationNumber &&
      formatOrganisationNumber(`${initialOrganisationNumber}`) ===
        formatOrganisationNumber(`${organisationNumber}`)
        ? (rest satisfies Partial<ISocietiesModel>)
        : (data satisfies Partial<ISocietiesModel>)
    societyId && mutatePatchSociety({ societyId, data: dataToSend })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <Modal show={show} size="xl">
      {{
        header: <ModalHeader onClose={close}>Uppdatera förening</ModalHeader>,
        body: (
          <ModalBody className="overflow-y-auto">
            {adminSociety && (
              <EditSocietyModalContent
                onSubmit={onSubmit}
                onError={onError}
                onClose={close}
                society={adminSociety}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
